import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronRight } from 'lucide-react';
import { siteConfig } from '@/config/site';
import { useLanguage } from '@/context/LanguageContext';
import { api } from '@/services/api';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

// Handbook data structure - English
const handbookSectionsEN = [
  {
    id: 'core-ten',
    code: '01',
    title: 'Core Ten of Casa de Candy',
    subtitle: 'Foundation rules that apply to every role, every shift, and every operational decision inside the Casa.',
    items: [
      { code: '01', label: 'Alignment', title: 'The Casa Comes First', text: 'All decisions must protect the identity, continuity, and operational stability of Casa de Candy above individual preference.' },
      { code: '02', label: 'Confidentiality', title: 'Confidentiality Is the Foundation', text: 'Client data, internal methods, pricing, communications, and operational strategy remain internal and fully protected under NDA.' },
      { code: '03', label: 'Structure', title: 'Loyalty to the System', text: 'Employees follow approved workflows and instructions. Critical tasks are executed as designed, not improvised.' },
      { code: '04', label: 'Boundaries', title: 'Controlled Access', text: 'Each role operates strictly within its assigned access level. Unauthorized access or use of systems is prohibited.' },
      { code: '05', label: 'Standards', title: 'Professionalism Always', text: 'All communication, presentation, and interaction — internal or client-facing — must reflect Casa de Candy standards.' },
      { code: '06', label: 'Stability', title: 'Zero Tolerance for Disruption', text: 'Harassment, dishonesty, unsafe conduct, or destabilizing behavior triggers immediate review and corrective action.' },
      { code: '07', label: 'Clarity', title: 'Accuracy & Documentation', text: 'Logs, schedules, records, and reports must be accurate, complete, and aligned with documented procedures.' },
      { code: '08', label: 'Integrity', title: 'No Competing Interests', text: 'Employees may not redirect clients, build competing operations, or use Casa knowledge for external gain.' },
      { code: '09', label: 'Respect', title: 'Discretion & Internal Integrity', text: 'Internal respect, discretion, and boundary awareness are mandatory on-site, off-site, and online.' },
      { code: '10', label: 'Growth', title: 'Growth Through Structure', text: 'Scaling is driven by process, control, and consistency — not by shortcuts or unmanaged risk.' },
    ]
  },
  {
    id: 'section-a',
    code: 'A',
    title: 'Welcome to Casa de Candy',
    tag: 'Brand identity & culture',
    subtitle: 'This section defines who we are, how Casa de Candy operates, and the standards that guide our daily operations, brand, and environment.',
    subsections: [
      { code: 'A.1', title: 'Introduction & Brand Overview', items: ['Casa de Candy identity and positioning.', 'Service philosophy and experience standards.', 'What "Casa" means operationally for staff and clients.'] },
      { code: 'A.2', title: 'Mission, Vision & Long-Term Goals', items: ['Core mission and long-term objectives.', 'How employees support strategic growth.', 'Expectations for alignment with Casa direction.'] },
      { code: 'A.3', title: 'Organizational Structure', items: ['Ownership and operational structure.', 'Legal and operational implications for staff.', 'Where decisions originate and how they flow.'] },
      { code: 'A.4', title: 'Casa Culture & Professional Expectations', items: ['Expected attitude, demeanor, and energy inside the Casa.', 'Professionalism standards beyond minimum requirements.', 'How culture ties directly to client retention and revenue.'] },
    ]
  },
  {
    id: 'section-b',
    code: 'B',
    title: 'Employment Framework',
    tag: 'Structure of employment',
    subtitle: 'Defines how employment is categorized, governed, and protected, including fundamental legal and ethical obligations on both sides.',
    subsections: [
      { code: 'B.1', title: 'Employee Classifications', items: ['Probationary, part-time, full-time, and contracted roles.', 'How each classification affects access and expectations.'] },
      { code: 'B.2', title: 'Equal Opportunity Statement', items: ['Non-discrimination policy and commitments.', 'Protected classes and legal compliance.'] },
      { code: 'B.3', title: 'Anti-Harassment & Anti-Discrimination Policy', items: ['Definition of harassment and prohibited conduct.', 'Reporting channels and investigation process.', 'Consequences and protections against retaliation.'] },
      { code: 'B.4', title: 'Workplace Safety & Security Standards', items: ['Physical safety expectations.', 'Security protocols for staff and clients.'] },
    ]
  },
  {
    id: 'section-c',
    code: 'C',
    title: 'Onboarding & Probation',
    tag: 'Entry into the Casa',
    subtitle: 'Covers how applicants become staff, the protections that must be signed, and the 90-day probationary structure before full engagement.',
    subsections: [
      { code: 'C.1', title: 'Application Procedures', items: ['Onboarding form requirements.', 'Information verification and screening.'] },
      { code: 'C.2', title: 'Mandatory NDA & Confidentiality Agreement', items: ['When NDA is signed.', 'Scope of covered information.', 'Consequences for breach.'] },
      { code: 'C.3', title: 'Non-Compete & Non-Solicitation Agreement', items: ['Limitations on competing activity.', 'Client and staff non-solicitation rules.'] },
      { code: 'C.4', title: '90-Day Probationary Period', items: ['Performance, behavior, and reliability standards.', 'Restricted access (probationary tier).', 'Review process and criteria for conversion.', 'Transition to full engagement contract.'] },
    ]
  },
  {
    id: 'section-d',
    code: 'D',
    title: 'Conduct, Appearance & Presentation',
    tag: 'How the Casa presents itself',
    subtitle: 'Establishes the behavioral, visual, and communication standards that govern every interaction connected to Casa de Candy.',
    subsections: [
      { code: 'D.1', title: 'Professional Conduct Expectations', items: ['On-shift behavior standards.', 'Respectful interaction rules.'] },
      { code: 'D.2', title: 'Grooming & Appearance Standards', items: ['Baseline appearance requirements.', 'Brand-consistent style expectations.'] },
      { code: 'D.3', title: 'Communication Protocols', items: ['How messages, calls, and digital communication are handled.', 'Tone, response time, and documentation.'] },
      { code: 'D.4', title: 'Social Media & Public Conduct Restrictions', items: ['What may and may not be posted about Casa.', 'Use of brand references and media.'] },
    ]
  },
  {
    id: 'section-e',
    code: 'E',
    title: 'Operational Structure & Workflow',
    tag: 'How work moves through the Casa',
    subtitle: 'Defines the way tasks, clients, and information move through the system and how employees are expected to plug into that structure.',
    subsections: [
      { code: 'E.1', title: 'Workflow Compliance', items: ['Why standardized procedures are mandatory.', 'Examples of compliant vs. non-compliant behavior.'] },
      { code: 'E.2', title: 'Communication Flow & Escalation', items: ['Who employees report to and in what order.', 'How to escalate issues or sensitive situations.'] },
      { code: 'E.3', title: 'Scheduling & Task Management', items: ['Schedule adherence and change requests.', 'How tasks are assigned, tracked, and closed.'] },
      { code: 'E.4', title: 'Performance Metrics (KPIs)', items: ['What is measured and why.', 'How performance connects to incentives and risk.'] },
    ]
  },
  {
    id: 'section-f',
    code: 'F',
    title: 'Access & Security Controls',
    tag: 'Information & system protection',
    subtitle: 'Specifies access levels, security protocols, and how digital and physical systems must be protected by all employees.',
    subsections: [
      { code: 'F.1', title: 'Access Tiers (Tier 1–3)', items: ['What each tier can and cannot access.', 'How tier changes are granted or revoked.'] },
      { code: 'F.2', title: 'Device & Account Security', items: ['Allowed devices and basic security requirements.', 'Password, login, and session rules.'] },
      { code: 'F.3', title: 'Data Handling & Confidential Information', items: ['How client and operational data is stored, used, and shared.', 'Prohibited behaviors with screenshots, exports, or external storage.'] },
      { code: 'F.4', title: 'Monitoring, Audits & Incident Reporting', items: ['Awareness of monitoring and log review.', 'How to report security concerns or breaches.'] },
    ]
  },
  {
    id: 'section-g',
    code: 'G',
    title: 'Compensation & Administration',
    tag: 'Pay, time & records',
    subtitle: 'Outlines how compensation, scheduling, and administrative details are managed and what staff should expect from the organization.',
    subsections: [
      { code: 'G.1', title: 'Compensation Structure & Pay Periods', items: ['Base pay, commissions, or incentives as applicable.', 'Payment schedule and methods.'] },
      { code: 'G.2', title: 'Time Tracking, Attendance & Punctuality', items: ['How time is recorded.', 'Expectations for punctuality and notice for absences.'] },
      { code: 'G.3', title: 'Leave, Breaks & Time-Off Policies', items: ['Break rules per shift.', 'How to request time off.'] },
      { code: 'G.4', title: 'Administrative Recordkeeping', items: ['Employee files and documentation.', 'Updates to personal or banking information.'] },
    ]
  },
  {
    id: 'section-h',
    code: 'H',
    title: 'Behavioral Standards & Discipline',
    tag: 'Response to violations',
    subtitle: 'Defines what happens when expectations are not met, including investigation, corrective action, and separation procedures.',
    subsections: [
      { code: 'H.1', title: 'Policy Violations & Consequences', items: ['Minor vs. major violations.', 'Examples tied to Core Ten rules.'] },
      { code: 'H.2', title: 'Investigative Process', items: ['How concerns are reviewed.', 'Employee rights during investigation.'] },
      { code: 'H.3', title: 'Corrective Action & Termination', items: ['Verbal, written, suspension and termination pathways.', 'Immediate termination triggers (e.g., breach of NDA, theft, or violence).'] },
      { code: 'H.4', title: 'Exit Procedures & Post-Engagement Obligations', items: ['Return of property and access removal.', 'Continuing NDA and non-compete obligations.'] },
    ]
  },
  {
    id: 'section-i',
    code: 'I',
    title: 'Brand Integrity & External Relations',
    tag: 'How the Casa shows up externally',
    subtitle: 'Covers how Casa de Candy is represented in all external-facing situations, from clients to vendors to potential partners.',
    subsections: [
      { code: 'I.1', title: 'Brand Representation Requirements', items: ['Use of Casa de Candy name, imagery, and materials.', 'Who may speak on behalf of the Casa.'] },
      { code: 'I.2', title: 'Client Relations Protocol', items: ['Baseline client interaction rules.', 'Handling complaints, issues, and escalations.'] },
      { code: 'I.3', title: 'Crisis & Reputation Management', items: ['What staff must do in high-risk or sensitive situations.', 'Who controls external messaging and decisions.'] },
    ]
  },
  {
    id: 'section-j',
    code: 'J',
    title: 'Future Growth Structure',
    tag: 'Paths forward inside the Casa',
    subtitle: "Lays out how employees can move up, expand responsibilities, and align with Casa de Candy's long-term build-out.",
    subsections: [
      { code: 'J.1', title: 'Advancement Opportunities', items: ['Criteria for being considered for promotions or expanded roles.', 'Connection between reliability, performance, and access.'] },
      { code: 'J.2', title: 'Leadership Track vs. Operational Track', items: ['Different growth paths available to employees.', 'Expectations for each path.'] },
      { code: 'J.3', title: 'Training, Certification & Role Expansion', items: ['How training ties to increased responsibility.', 'Documentation of role expansions and access tier changes.'] },
    ],
    note: 'This index is designed as the backbone. Each item expands into full, signed policy language.'
  },
];

// Handbook data structure - Spanish (Full Translation)
const handbookSectionsES = [
  {
    id: 'core-ten',
    code: '01',
    title: 'Los Diez Principios de Casa de Candy',
    subtitle: 'Reglas fundamentales que aplican a cada rol, cada turno y cada decisión operativa dentro de la Casa.',
    items: [
      { code: '01', label: 'Alineación', title: 'La Casa Primero', text: 'Todas las decisiones deben proteger la identidad, continuidad y estabilidad operativa de Casa de Candy por encima de las preferencias individuales.' },
      { code: '02', label: 'Confidencialidad', title: 'La Confidencialidad es la Base', text: 'Los datos de clientes, métodos internos, precios, comunicaciones y estrategia operativa permanecen internos y completamente protegidos bajo NDA.' },
      { code: '03', label: 'Estructura', title: 'Lealtad al Sistema', text: 'Los empleados siguen los flujos de trabajo e instrucciones aprobadas. Las tareas críticas se ejecutan según el diseño, sin improvisación.' },
      { code: '04', label: 'Límites', title: 'Acceso Controlado', text: 'Cada rol opera estrictamente dentro de su nivel de acceso asignado. El acceso no autorizado o uso de sistemas está prohibido.' },
      { code: '05', label: 'Estándares', title: 'Profesionalismo Siempre', text: 'Toda comunicación, presentación e interacción — interna o con clientes — debe reflejar los estándares de Casa de Candy.' },
      { code: '06', label: 'Estabilidad', title: 'Tolerancia Cero a la Disrupción', text: 'El acoso, deshonestidad, conducta insegura o comportamiento desestabilizador desencadena revisión inmediata y acción correctiva.' },
      { code: '07', label: 'Claridad', title: 'Precisión y Documentación', text: 'Los registros, horarios, archivos e informes deben ser precisos, completos y alineados con los procedimientos documentados.' },
      { code: '08', label: 'Integridad', title: 'Sin Intereses en Competencia', text: 'Los empleados no pueden redirigir clientes, construir operaciones competidoras o usar el conocimiento de Casa para ganancia externa.' },
      { code: '09', label: 'Respeto', title: 'Discreción e Integridad Interna', text: 'El respeto interno, discreción y conciencia de límites son obligatorios en el sitio, fuera del sitio y en línea.' },
      { code: '10', label: 'Crecimiento', title: 'Crecimiento a Través de Estructura', text: 'La expansión es impulsada por proceso, control y consistencia — no por atajos o riesgo no gestionado.' },
    ]
  },
  {
    id: 'section-a',
    code: 'A',
    title: 'Bienvenido a Casa de Candy',
    tag: 'Identidad de marca y cultura',
    subtitle: 'Esta sección define quiénes somos, cómo opera Casa de Candy y los estándares que guían nuestras operaciones diarias, marca y ambiente.',
    subsections: [
      { code: 'A.1', title: 'Introducción y Visión de Marca', items: ['Identidad y posicionamiento de Casa de Candy.', 'Filosofía de servicio y estándares de experiencia.', 'Lo que "Casa" significa operativamente para el personal y clientes.'] },
      { code: 'A.2', title: 'Misión, Visión y Objetivos a Largo Plazo', items: ['Misión central y objetivos a largo plazo.', 'Cómo los empleados apoyan el crecimiento estratégico.', 'Expectativas de alineación con la dirección de Casa.'] },
      { code: 'A.3', title: 'Estructura Organizacional', items: ['Estructura de propiedad y operativa.', 'Implicaciones legales y operativas para el personal.', 'De dónde se originan las decisiones y cómo fluyen.'] },
      { code: 'A.4', title: 'Cultura Casa y Expectativas Profesionales', items: ['Actitud, comportamiento y energía esperados dentro de la Casa.', 'Estándares de profesionalismo más allá de los requisitos mínimos.', 'Cómo la cultura se relaciona directamente con la retención de clientes e ingresos.'] },
    ]
  },
  {
    id: 'section-b',
    code: 'B',
    title: 'Marco de Empleo',
    tag: 'Estructura de empleo',
    subtitle: 'Define cómo se categoriza, gobierna y protege el empleo, incluyendo obligaciones legales y éticas fundamentales.',
    subsections: [
      { code: 'B.1', title: 'Clasificaciones de Empleados', items: ['Roles probatorios, tiempo parcial, tiempo completo y contratados.', 'Cómo cada clasificación afecta acceso y expectativas.'] },
      { code: 'B.2', title: 'Declaración de Igualdad de Oportunidades', items: ['Política de no discriminación y compromisos.', 'Clases protegidas y cumplimiento legal.'] },
      { code: 'B.3', title: 'Política Anti-Acoso y Anti-Discriminación', items: ['Definición de acoso y conducta prohibida.', 'Canales de reporte y proceso de investigación.', 'Consecuencias y protecciones contra represalias.'] },
      { code: 'B.4', title: 'Estándares de Seguridad Laboral', items: ['Expectativas de seguridad física.', 'Protocolos de seguridad para personal y clientes.'] },
    ]
  },
  {
    id: 'section-c',
    code: 'C',
    title: 'Incorporación y Período de Prueba',
    tag: 'Entrada a la Casa',
    subtitle: 'Cubre cómo los solicitantes se convierten en personal, las protecciones que deben firmarse y la estructura de prueba de 90 días.',
    subsections: [
      { code: 'C.1', title: 'Procedimientos de Solicitud', items: ['Requisitos del formulario de incorporación.', 'Verificación de información y selección.'] },
      { code: 'C.2', title: 'NDA Obligatorio y Acuerdo de Confidencialidad', items: ['Cuándo se firma el NDA.', 'Alcance de la información cubierta.', 'Consecuencias por incumplimiento.'] },
      { code: 'C.3', title: 'Acuerdo de No Competencia y No Solicitud', items: ['Limitaciones sobre actividad competidora.', 'Reglas de no solicitud de clientes y personal.'] },
      { code: 'C.4', title: 'Período de Prueba de 90 Días', items: ['Estándares de rendimiento, comportamiento y confiabilidad.', 'Acceso restringido (nivel probatorio).', 'Proceso de revisión y criterios de conversión.', 'Transición al contrato de compromiso completo.'] },
    ]
  },
  {
    id: 'section-d',
    code: 'D',
    title: 'Conducta, Apariencia y Presentación',
    tag: 'Cómo se presenta la Casa',
    subtitle: 'Establece los estándares de comportamiento, visuales y de comunicación que gobiernan cada interacción.',
    subsections: [
      { code: 'D.1', title: 'Expectativas de Conducta Profesional', items: ['Estándares de comportamiento durante el turno.', 'Reglas de interacción respetuosa.'] },
      { code: 'D.2', title: 'Estándares de Apariencia y Presentación', items: ['Requisitos básicos de apariencia.', 'Expectativas de estilo consistentes con la marca.'] },
      { code: 'D.3', title: 'Protocolos de Comunicación', items: ['Cómo se manejan mensajes, llamadas y comunicación digital.', 'Tono, tiempo de respuesta y documentación.'] },
      { code: 'D.4', title: 'Restricciones de Redes Sociales y Conducta Pública', items: ['Qué se puede y no se puede publicar sobre Casa.', 'Uso de referencias de marca y medios.'] },
    ]
  },
  {
    id: 'section-e',
    code: 'E',
    title: 'Estructura Operativa y Flujo de Trabajo',
    tag: 'Cómo fluye el trabajo en la Casa',
    subtitle: 'Define cómo las tareas, clientes e información se mueven a través del sistema.',
    subsections: [
      { code: 'E.1', title: 'Cumplimiento del Flujo de Trabajo', items: ['Por qué los procedimientos estandarizados son obligatorios.', 'Ejemplos de comportamiento conforme vs. no conforme.'] },
      { code: 'E.2', title: 'Flujo de Comunicación y Escalamiento', items: ['A quién reportan los empleados y en qué orden.', 'Cómo escalar problemas o situaciones sensibles.'] },
      { code: 'E.3', title: 'Programación y Gestión de Tareas', items: ['Adherencia al horario y solicitudes de cambio.', 'Cómo se asignan, rastrean y cierran las tareas.'] },
      { code: 'E.4', title: 'Métricas de Rendimiento (KPIs)', items: ['Qué se mide y por qué.', 'Cómo el rendimiento se conecta con incentivos y riesgo.'] },
    ]
  },
  {
    id: 'section-f',
    code: 'F',
    title: 'Controles de Acceso y Seguridad',
    tag: 'Protección de información y sistemas',
    subtitle: 'Especifica niveles de acceso, protocolos de seguridad y cómo deben protegerse los sistemas.',
    subsections: [
      { code: 'F.1', title: 'Niveles de Acceso (Nivel 1–3)', items: ['Qué puede y no puede acceder cada nivel.', 'Cómo se otorgan o revocan cambios de nivel.'] },
      { code: 'F.2', title: 'Seguridad de Dispositivos y Cuentas', items: ['Dispositivos permitidos y requisitos básicos de seguridad.', 'Reglas de contraseña, inicio de sesión y sesión.'] },
      { code: 'F.3', title: 'Manejo de Datos e Información Confidencial', items: ['Cómo se almacena, usa y comparte datos de clientes y operativos.', 'Comportamientos prohibidos con capturas, exportaciones o almacenamiento externo.'] },
      { code: 'F.4', title: 'Monitoreo, Auditorías y Reporte de Incidentes', items: ['Conciencia de monitoreo y revisión de registros.', 'Cómo reportar preocupaciones de seguridad o violaciones.'] },
    ]
  },
  {
    id: 'section-g',
    code: 'G',
    title: 'Compensación y Administración',
    tag: 'Pago, tiempo y registros',
    subtitle: 'Describe cómo se gestionan la compensación, programación y detalles administrativos.',
    subsections: [
      { code: 'G.1', title: 'Estructura de Compensación y Períodos de Pago', items: ['Pago base, comisiones o incentivos según corresponda.', 'Calendario y métodos de pago.'] },
      { code: 'G.2', title: 'Control de Tiempo, Asistencia y Puntualidad', items: ['Cómo se registra el tiempo.', 'Expectativas de puntualidad y aviso de ausencias.'] },
      { code: 'G.3', title: 'Políticas de Descanso, Pausas y Tiempo Libre', items: ['Reglas de descanso por turno.', 'Cómo solicitar tiempo libre.'] },
      { code: 'G.4', title: 'Mantenimiento de Registros Administrativos', items: ['Archivos y documentación de empleados.', 'Actualizaciones de información personal o bancaria.'] },
    ]
  },
  {
    id: 'section-h',
    code: 'H',
    title: 'Estándares de Conducta y Disciplina',
    tag: 'Respuesta a violaciones',
    subtitle: 'Define qué sucede cuando no se cumplen las expectativas, incluyendo investigación, acción correctiva y procedimientos de separación.',
    subsections: [
      { code: 'H.1', title: 'Violaciones de Política y Consecuencias', items: ['Violaciones menores vs. mayores.', 'Ejemplos vinculados a las reglas de los Diez Principios.'] },
      { code: 'H.2', title: 'Proceso de Investigación', items: ['Cómo se revisan las preocupaciones.', 'Derechos del empleado durante la investigación.'] },
      { code: 'H.3', title: 'Acción Correctiva y Terminación', items: ['Vías de advertencia verbal, escrita, suspensión y terminación.', 'Desencadenantes de terminación inmediata (ej., violación de NDA, robo o violencia).'] },
      { code: 'H.4', title: 'Procedimientos de Salida y Obligaciones Post-Compromiso', items: ['Devolución de propiedad y eliminación de acceso.', 'Obligaciones continuas de NDA y no competencia.'] },
    ]
  },
  {
    id: 'section-i',
    code: 'I',
    title: 'Integridad de Marca y Relaciones Externas',
    tag: 'Cómo la Casa se presenta externamente',
    subtitle: 'Cubre cómo Casa de Candy se representa en todas las situaciones externas.',
    subsections: [
      { code: 'I.1', title: 'Requisitos de Representación de Marca', items: ['Uso del nombre, imagen y materiales de Casa de Candy.', 'Quién puede hablar en nombre de la Casa.'] },
      { code: 'I.2', title: 'Protocolo de Relaciones con Clientes', items: ['Reglas básicas de interacción con clientes.', 'Manejo de quejas, problemas y escalamientos.'] },
      { code: 'I.3', title: 'Crisis y Gestión de Reputación', items: ['Qué debe hacer el personal en situaciones sensibles o de alto riesgo.', 'Quién controla los mensajes externos y decisiones.'] },
    ]
  },
  {
    id: 'section-j',
    code: 'J',
    title: 'Estructura de Crecimiento Futuro',
    tag: 'Caminos hacia adelante dentro de la Casa',
    subtitle: 'Establece cómo los empleados pueden avanzar, expandir responsabilidades y alinearse con el crecimiento de Casa de Candy.',
    subsections: [
      { code: 'J.1', title: 'Oportunidades de Avance', items: ['Criterios para ser considerado para promociones o roles expandidos.', 'Conexión entre confiabilidad, rendimiento y acceso.'] },
      { code: 'J.2', title: 'Pista de Liderazgo vs. Pista Operativa', items: ['Diferentes caminos de crecimiento disponibles para empleados.', 'Expectativas para cada camino.'] },
      { code: 'J.3', title: 'Capacitación, Certificación y Expansión de Rol', items: ['Cómo la capacitación se vincula con mayor responsabilidad.', 'Documentación de expansiones de rol y cambios de nivel de acceso.'] },
    ],
    note: 'Este índice está diseñado como la columna vertebral. Cada elemento se expande en lenguaje de política completo y firmado.'
  },
];

export function HandbookPage() {
  const { language, toggleLanguage } = useLanguage();
  
  // Use appropriate language sections - Spanish translations now available
  const handbookSections = language === 'en' ? handbookSectionsEN : handbookSectionsES;

  useEffect(() => {
    api.trackEvent('page_view', 'handbook', '/handbook');
  }, []);

  return (
    <>
      <Helmet>
        <title>{siteConfig.seo.handbook.title}</title>
        <meta name="description" content={siteConfig.seo.handbook.description} />
        <html lang={language} />
      </Helmet>

      <div className="min-h-screen bg-[#07060B] flex flex-col" data-testid="handbook-page">
        <Navbar />
        
        <main className="flex-1 pt-24 pb-16">
          {/* Header */}
          <header className="py-12 px-4 sm:px-6 lg:px-8 border-b border-white/10">
            <div className="max-w-5xl mx-auto text-center">
              {/* Logo - Clean with bright glow */}
              <div className="relative inline-block mb-4">
                <img 
                  src={siteConfig.brand.logoUrl}
                  alt="Casa de Candy"
                  className="h-16 mx-auto object-contain"
                  style={{
                    filter: 'drop-shadow(0 0 12px rgba(139, 92, 255, 0.8)) drop-shadow(0 0 25px rgba(255, 77, 166, 0.5))'
                  }}
                />
              </div>
              <p className="text-[#A7A3BA] text-xs uppercase tracking-widest mb-2">
                CASA DE CANDY · INTERNAL
              </p>
              <p className="text-[#8B5CFF] text-xs mb-4">
                {language === 'en' ? 'Instruction by Casa de Candy' : 'Instrucción por Casa de Candy'}
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#F4F2FF] mb-4">
                {language === 'en' ? 'Employee Handbook Index' : 'Índice del Manual del Empleado'}
              </h1>
              <p className="text-[#A7A3BA] max-w-2xl mx-auto">
                {language === 'en' 
                  ? 'This document defines the core standards, structure, and reference sections that govern engagement, conduct, and operational expectations within Casa de Candy.'
                  : 'Este documento define los estándares centrales, estructura y secciones de referencia que gobiernan el compromiso, conducta y expectativas operativas dentro de Casa de Candy.'}
              </p>
              
              {/* Language Toggle */}
              <div className="mt-6 flex items-center justify-center gap-4">
                <button
                  onClick={toggleLanguage}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#111022] border border-white/10 hover:border-[#8B5CFF]/50 transition-colors"
                  data-testid="handbook-language-toggle"
                >
                  <span className="w-2 h-2 rounded-full bg-[#27D7FF]" />
                  <span className="text-xs text-[#A7A3BA]">
                    {language === 'en' ? 'Switch to Spanish' : 'Cambiar a Inglés'}
                  </span>
                </button>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#111022] border border-white/10">
                  <span className="w-2 h-2 rounded-full bg-[#27D7FF]" />
                  <span className="text-xs text-[#A7A3BA]">v1.0 · {language === 'en' ? 'Internal Use Only' : 'Solo Uso Interno'}</span>
                </div>
              </div>
            </div>
          </header>

          {/* Content Grid */}
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Table of Contents - Sidebar */}
              <aside className="lg:col-span-1">
                <div className="neon-card sticky top-24">
                  <p className="text-[#A7A3BA] text-xs uppercase tracking-widest mb-2">
                    {language === 'en' ? 'Navigation' : 'Navegación'}
                  </p>
                  <h3 className="font-heading font-semibold text-[#F4F2FF] mb-4">
                    {language === 'en' ? 'Handbook Overview' : 'Resumen del Manual'}
                  </h3>
                  <p className="text-xs text-[#8B5CFF] mb-4">
                    {language === 'en' ? 'Core Ten + Sections A–J' : 'Diez Principios + Secciones A–J'}
                  </p>
                  <div className="h-px bg-gradient-to-r from-[#FF4DA6] to-transparent mb-4" />
                  <ul className="space-y-2 max-h-96 overflow-y-auto">
                    {handbookSections.map((section) => (
                      <li key={section.id}>
                        <a
                          href={`#${section.id}`}
                          className="flex items-center gap-2 text-sm text-[#A7A3BA] hover:text-[#FF4DA6] transition-colors py-1"
                          data-testid={`toc-${section.id}`}
                        >
                          <span className="text-[#FF4DA6] font-mono text-xs">{section.code}</span>
                          <span className="truncate">{section.title}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>

              {/* Main Content */}
              <div className="lg:col-span-3 space-y-8">
                {handbookSections.map((section) => (
                  <section key={section.id} id={section.id} className="neon-card" data-testid={`handbook-${section.id}`}>
                    {/* Section Header */}
                    <div className="mb-6">
                      <p className="text-[#FF4DA6] text-xs uppercase tracking-widest mb-1">
                        {language === 'en' ? 'Section' : 'Sección'} {section.code}
                      </p>
                      <h2 className="font-heading text-2xl font-bold text-[#F4F2FF] mb-2">
                        {section.title}
                      </h2>
                      {section.tag && (
                        <span className="inline-block px-3 py-1 rounded-full bg-[#8B5CFF]/20 text-[#8B5CFF] text-xs mb-2">
                          {section.tag}
                        </span>
                      )}
                      <p className="text-[#A7A3BA] text-sm">{section.subtitle}</p>
                    </div>

                    {/* Core Ten Items */}
                    {section.items && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {section.items.map((item, idx) => (
                          <div key={idx} className="p-4 rounded-xl bg-[#0B0A12] border border-white/5">
                            <div className="flex items-start gap-3">
                              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#FF4DA6]/20 to-[#8B5CFF]/20 flex items-center justify-center text-[#FF4DA6] font-bold text-xs flex-shrink-0">
                                {item.code}
                              </span>
                              <div>
                                <p className="text-[#8B5CFF] text-xs uppercase tracking-wider mb-1">{item.label}</p>
                                <h4 className="text-[#F4F2FF] font-medium text-sm mb-1">{item.title}</h4>
                                <p className="text-[#A7A3BA] text-xs leading-relaxed">{item.text}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Subsections */}
                    {section.subsections && (
                      <div className="space-y-4">
                        {section.subsections.map((sub, idx) => (
                          <details key={idx} className="group">
                            <summary className="flex items-center justify-between cursor-pointer py-3 px-4 rounded-xl bg-[#0B0A12] border border-white/5 hover:border-[#FF4DA6]/30 transition-colors">
                              <span className="flex items-center gap-3">
                                <span className="text-[#FF4DA6] font-mono text-sm">{sub.code}</span>
                                <span className="text-[#F4F2FF] font-medium">{sub.title}</span>
                              </span>
                              <ChevronRight className="w-4 h-4 text-[#8B5CFF] transition-transform group-open:rotate-90" />
                            </summary>
                            <div className="mt-2 ml-4 pl-6 border-l border-white/10 py-3">
                              <ul className="space-y-2">
                                {sub.items.map((item, i) => (
                                  <li key={i} className="flex items-start gap-2 text-sm text-[#A7A3BA]">
                                    <span className="text-[#27D7FF]">•</span>
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </details>
                        ))}
                      </div>
                    )}

                    {/* Note */}
                    {section.note && (
                      <p className="mt-6 text-xs text-[#A7A3BA] italic border-t border-white/10 pt-4">
                        {language === 'en' ? 'Note' : 'Nota'}: {section.note}
                      </p>
                    )}
                  </section>
                ))}
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}

export default HandbookPage;
